import { Check, Copy, ExternalLink, Info, Ticket } from 'lucide-react';
import { useState } from 'react';

import type { CupomDaVitrineResponse } from '@/api/generated/model';
import { useCopiarCodigo } from '@/features/clube/hooks/useCopiarCodigo';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

/**
 * Card de cupom da vitrine (F5-6).
 *
 * Uma troca em relação à tela da V1, por falta de dado — não vale uma coluna
 * nova em `Cupom` só para manter a paridade visual: onde havia o NOME DO
 * PARCEIRO, entra `descricao`. O cupom não tem parceiro no modelo; o slot é
 * uma linha secundária, e `descricao` é exatamente isso.
 *
 * ⚠️ Resgate por código OU por link (ajuste de Ago/2026) — nunca os dois
 * juntos no formulário do admin, mas nada impede um cupom antigo com só
 * código e um novo com só link de conviverem na mesma grade.
 *
 * ⚠️ Mesma técnica do logo do parceiro (`ParceirosPage.tsx`), no card E no
 * modal de detalhes (ajuste de Ago/2026): moldura em `aspect-[3/1]` — a
 * proporção que o upload já exige (`CampoDeImagem` no admin recorta pra isso)
 * — com `object-contain` sem padding. Quadro e imagem SEMPRE compartilham a
 * proporção, então preenche exato: sem sobra de fundo branco, sem cortar
 * nada. (Já tentamos altura fixa + `object-cover` — cortava a arte; e
 * `aspect-square` — ficava alto demais. `aspect-[3/1]` é o que faz o card do
 * parceiro funcionar bem, e cupom tem a mesma exigência de proporção 3:1.)
 */
export function CardDeCupom({
  cupom,
  aoCopiar,
}: {
  cupom: CupomDaVitrineResponse;
  /** Registra o resgate. Chamado quando a cópia do código dá certo, ou quando o link é clicado. */
  aoCopiar: (id: string) => void;
}) {
  const [detalhesAbertos, setDetalhesAbertos] = useState(false);

  return (
    <article className="bg-surface-raised rounded-xl overflow-hidden border border-border hover:shadow-lg transition-shadow flex flex-col">
      {/* Fundo branco, e não `bg-surface-raised` como no card do parceiro: a
          arte do cupom costuma ter fundo transparente e sumiria contra o card
          no tema escuro. */}
      <div className="aspect-[3/1] bg-white flex items-center justify-center border-b border-border">
        {cupom.imagemUrl ? (
          <img src={cupom.imagemUrl} alt={cupom.titulo} className="w-full h-full object-contain" />
        ) : (
          <Ticket className="w-12 h-12 text-muted-foreground/40" />
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground truncate">{cupom.descricao ?? ' '}</p>
          {cupom.categoria && (
            <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-brand/10 text-brand">
              {cupom.categoria.nome}
            </span>
          )}
        </div>

        <h3 className="text-base font-bold text-foreground mt-0.5">{cupom.titulo}</h3>

        <p className="text-xs text-muted-foreground mt-2">
          {cupom.validoAte ? `Válido até ${formatarData(cupom.validoAte)}` : 'Sem prazo de validade'}
        </p>

        {/* Mesmo padrão do card de parceiro (`ParceirosPage.tsx`): dois
            botões flex-1 dividindo a linha, "mais detalhes" com fundo
            translúcido e o de resgate sólido. */}
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={() => setDetalhesAbertos(true)}
            className="flex-1 flex items-center justify-center gap-2 h-9 text-sm font-medium text-brand bg-brand/10 hover:bg-brand/15 rounded-lg transition-colors"
          >
            <Info className="w-4 h-4" />
            Mais detalhes
          </button>

          <div className="flex-1">
            <ResgateDoCupom cupom={cupom} aoCopiar={aoCopiar} />
          </div>
        </div>
      </div>

      <Dialog open={detalhesAbertos} onOpenChange={setDetalhesAbertos}>
        <DialogContent className="sm:max-w-md">
          <div className="aspect-[3/1] -mx-6 -mt-6 mb-2 bg-white flex items-center justify-center border-b border-border">
            {cupom.imagemUrl ? (
              <img src={cupom.imagemUrl} alt={cupom.titulo} className="w-full h-full object-contain" />
            ) : (
              <Ticket className="w-16 h-16 text-muted-foreground/40" />
            )}
          </div>

          <DialogHeader>
            <div className="flex items-center gap-2">
              <DialogTitle>{cupom.titulo}</DialogTitle>
              {cupom.categoria && (
                <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-brand/10 text-brand">
                  {cupom.categoria.nome}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {cupom.validoAte ? `Válido até ${formatarData(cupom.validoAte)}` : 'Sem prazo de validade'}
            </p>
            <DialogDescription className="whitespace-pre-line text-foreground/80">
              {cupom.descricao ?? 'Sem descrição.'}
            </DialogDescription>
          </DialogHeader>

          <ResgateDoCupom cupom={cupom} aoCopiar={aoCopiar} />
        </DialogContent>
      </Dialog>
    </article>
  );
}

/** Bloco de resgate — código com botão de copiar, ou link de redirecionamento. Compartilhado pelo card e pelo modal de detalhes. */
function ResgateDoCupom({
  cupom,
  aoCopiar,
}: {
  cupom: CupomDaVitrineResponse;
  aoCopiar: (id: string) => void;
}) {
  const { copiado, copiar } = useCopiarCodigo(() => aoCopiar(cupom.id));

  if (cupom.codigo) {
    return (
      <div className="h-9 flex items-stretch gap-2">
        <div className="flex-1 min-w-0 flex items-center px-3 rounded-lg bg-muted border border-border">
          <span className="text-sm font-mono font-semibold text-foreground truncate">
            {cupom.codigo}
          </span>
        </div>
        <button
          type="button"
          onClick={() => void copiar(cupom.codigo as string)}
          title={copiado ? 'Copiado!' : 'Copiar código'}
          aria-label={copiado ? 'Copiado!' : 'Copiar código'}
          className="shrink-0 flex items-center justify-center h-9 w-9 rounded-lg text-brand-foreground bg-brand hover:bg-brand/80 transition-colors"
        >
          {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    );
  }

  if (!cupom.linkRedirecionamento) return null;

  return (
    <a
      href={cupom.linkRedirecionamento}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => aoCopiar(cupom.id)}
      className="flex items-center justify-center gap-2 h-9 rounded-lg text-brand-foreground bg-brand hover:bg-brand/80 transition-colors text-sm font-medium"
    >
      <ExternalLink className="w-4 h-4" />
      Abrir link
    </a>
  );
}

function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}
